// The skm-flip-slide web component (loaded globally from skm-components on
// the CDN) hardcodes a "Insights" header text inside its shadow DOM. The
// header is not exposed as an editable property and admins cannot customize
// it through the lesson editor, so we hide it everywhere — both in the
// admin editor and in student view — via a stylesheet injected into each
// instance's shadow root.
//
// We also recolor .slide-trigger ("Click to flip") to white and force a
// flex-column layout on each face so the trigger always sits below the
// content instead of overlapping it. Without this the bundle's default
// trigger position lands on top of long content.

const SHADOW_RULE = `
  .slide-header { display: none !important; }

  /* Each face becomes a flex column: content stretches and scrolls,
     trigger is pinned to the bottom. Works whether the bundle's default
     trigger is in flow or absolutely positioned (we override both). */
  .flip-box-front,
  .flip-box-back {
    display: flex !important;
    flex-direction: column !important;
    box-sizing: border-box;
  }
  .flip-box-front .slide-content,
  .flip-box-back .slide-content {
    flex: 1 1 auto;
    min-height: 0;
    /* Vertical scrolling only. "overflow: auto" (both axes) let long unbroken
       words trigger a horizontal scrollbar in the learner view; the bundle
       default (and the admin editor) only scroll vertically. Pair with
       overflow-wrap so long words wrap instead of being clipped (CON-187). */
    overflow-x: hidden;
    overflow-y: auto;
    overflow-wrap: break-word;
    word-break: break-word;
    position: relative !important;
    inset: auto !important;
    /* Default flip-card layout: text is large and centered both ways
       unless the author explicitly overrides via CKEditor's font-size /
       alignment toolbar (those emit inline styles with higher specificity,
       so per-run overrides still win). Use rem (root-relative) instead
       of em so the size matches between admin editor and learner view —
       em would compound against whichever parent context wraps the card. */
    display: flex;
    flex-direction: column;
    /* "safe" centers short content but falls back to start-alignment once
       the content overflows, so the top stays reachable and the whole card
       scrolls instead of clipping (CON-187). */
    justify-content: safe center;
    text-align: center;
    font-size: 2.25rem;
    /* Horizontal padding keeps text off the card edges and the scrollbar;
       asymmetric top padding nudges centered content slightly below
       visual-center to balance the bottom .slide-trigger (CON-187). */
    padding: 1.5rem 1.5rem 0;
  }
  .flip-box-front .slide-trigger,
  .flip-box-back .slide-trigger {
    position: relative !important;
    inset: auto !important;
    margin-top: auto;
    flex: 0 0 auto;
    padding: 0.5rem 0.75rem;
    text-align: center;
    color: #1C252E !important;
    font-weight: 600;
  }
  /* Dark-mode override — :host-context() reaches across the shadow
     boundary to find the page's .dark class, so the trigger tracks
     the site theme without us having to wire anything from the host. */
  :host-context(.dark) .flip-box-front .slide-trigger,
  :host-context(.dark) .flip-box-back .slide-trigger {
    color: #ffffff !important;
  }
  .slide-trigger * {
    color: inherit !important;
    fill: currentColor !important;
  }
  .slide-trigger:hover { opacity: 0.8; }
`;
const injected = new WeakSet();

const inject = (el) => {
  if (injected.has(el)) return;
  const root = el.shadowRoot;
  if (!root) return;
  injected.add(el);
  const style = document.createElement('style');
  style.dataset.flipSlideHeaderHide = 'true';
  style.textContent = SHADOW_RULE;
  root.appendChild(style);

  // Wheel/trackpad scrolling: the flip card uses a 3D transform, so the
  // browser won't route wheel events to the shadow `.slide-content` scroll
  // container on its own (only dragging the bar worked). We scroll it manually.
  //
  // We can't pick the target from the event path: the front face has
  // z-index: 2 and, in this preserve-3d context, keeps capturing the wheel
  // even when it's rotated away — so composedPath() would scroll the hidden
  // front while the visible back stays put. Instead, read the flip state
  // (the bundle adds `.rotation` to `.flip-box-inner` when the back is
  // showing) and scroll whichever face is actually visible (CON-187).
  el.addEventListener(
    'wheel',
    (e) => {
      const inner = root.querySelector('.flip-box-inner');
      const faceSel = inner?.classList.contains('rotation')
        ? '.flip-box-back'
        : '.flip-box-front';
      const sc = root.querySelector(`${faceSel} .slide-content`);
      if (!sc || sc.scrollHeight <= sc.clientHeight) return;
      // Normalize line-mode wheels (deltaMode 1 reports lines, not pixels)
      // so they don't crawl.
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      // Release to the page once the card is at its edge in the wheel's
      // direction, so an overflowing card doesn't trap page scrolling.
      const atTop = sc.scrollTop <= 0 && dy < 0;
      const atBottom =
        sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1 &&
        dy > 0;
      if (atTop || atBottom) return;
      sc.scrollTop += dy;
      e.preventDefault();
    },
    { passive: false }
  );
};

const apply = (el) => {
  if (el.tagName?.toLowerCase() !== 'skm-flip-slide') return;
  if (el.updateComplete?.then) {
    el.updateComplete.then(() => inject(el));
  } else {
    inject(el);
  }
};

export const installFlipSlideHeaderHider = () => {
  const sweep = () => {
    document.querySelectorAll('skm-flip-slide').forEach(apply);
  };

  sweep();
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        apply(n);
        n.querySelectorAll?.('skm-flip-slide').forEach(apply);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
};
